import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import Typography from '@/components/Typography';
import LoadData from '@/components/LoadData';
import { getOrganization, updateOrganizationAccess } from '@/services/api/organizations';
import Loading from '@/components/Loading';
import If from '@/components/If';
import Placeholder from '@/components/Placeholder';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

const formSchema = z.object({
  whitelistedDomains: z.string().min(1, 'Whitelist Domains is required')
});

export interface Props {
  id: string;
}

const OrganizationAccessForm = ({ organization, id }: { organization: any; id: string }) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      whitelistedDomains: organization.whitelistedDomains
    }
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      await updateOrganizationAccess(id, values.whitelistedDomains);
    } catch (error) {
      console.error('Failed to update organization access:', error);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="whitelistedDomains"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Whitelisted Domains</FormLabel>
              <FormControl>
                <Textarea placeholder="example.com; foo.com" className="resize-none" rows={3} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={form.formState.isSubmitting}>
          Save
        </Button>
      </form>
    </Form>
  );
};

const OrganizationAccessTab: React.FC<Props> = ({ id }) => {
  return (
    <Card>
      <CardHeader>
        <Typography is="h1" size="xl" weight="bold">
          Access
        </Typography>
        <Typography is="p" size="sm">
          Whitelist domains to allow access members to sign up into the organization without having to send out
          invitation emails.
        </Typography>
      </CardHeader>
      <CardContent>
        <LoadData id={id} load={getOrganization}>
          {({ value: organization, loading }) => (
            <If
              condition={loading}
              then={() => <Loading visible={true} />}
              else={() => (
                <If
                  condition={!organization}
                  then={() => (
                    <Placeholder
                      className="h-full justify-center"
                      image="bell"
                      size="xs"
                      title="Oups"
                      description="Organization not found. Please try again later."
                    />
                  )}
                  else={() => <OrganizationAccessForm organization={organization} id={id} />}
                />
              )}
            />
          )}
        </LoadData>
      </CardContent>
    </Card>
  );
};

export default OrganizationAccessTab;
